import React, { useEffect } from 'react';
import { X } from 'lucide-react';

export const Modal = ({ open, onClose, title, children, footer, width = 560 }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  // Lukk kun naar bruker faktisk klikker direkte paa overlay (ikke ved drag fra input til utsiden,
  // ikke naar select-dropdown lukkes utenfor modal, etc.). Sammenligner target vs currentTarget
  // paa mousedown for aa unngaa at tekstmarkering inne i input lukker modalen.
  const overlayMouseDown = (e) => { if (e.target === e.currentTarget) onClose?.(); };
  return (
    <div
      onMouseDown={overlayMouseDown}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="card"
        style={{ width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }}
      >
        <div className="flex items-center justify-between"
             style={{ padding: '12px 16px', borderBottom: '1px solid var(--theme-border)' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</h3>
          <button className="btn-ghost" onClick={onClose} style={{ height: 24, padding: '0 6px' }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
        {footer && (
          <div className="flex justify-end" style={{ gap: 8, padding: 12, borderTop: '1px solid var(--theme-border)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--theme-text-primary)' }}>{title}</h1>
      {subtitle && <p className="fm-text-muted" style={{ margin: '4px 0 0', fontSize: 12 }}>{subtitle}</p>}
    </div>
    {actions && <div className="flex" style={{ gap: 8 }}>{actions}</div>}
  </div>
);

export const Card = ({ children, style, ...rest }) => (
  <div className="card" style={{ padding: 16, ...style }} {...rest}>{children}</div>
);

export const StatCard = ({ label, value, color = 'var(--theme-accent)', icon: Icon }) => (
  <div className="card" style={{ padding: 16 }}>
    <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
      <span className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</span>
      {Icon && <Icon size={16} style={{ color }} />}
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
  </div>
);

/**
 * Tabs - uniform sub-navigation across all pages.
 * items: Array of { key, label, icon?: ReactComponent, badge?: number|string }
 * value: current selected key
 * onChange: (key) => void
 *
 * Stil styres globalt via .tabs / .tab / .tab--active i index.css.
 */
export const Tabs = ({ items, value, onChange, style, ...rest }) => (
  <div className="tabs" style={style} role="tablist" {...rest}>
    {items.map((it) => {
      if (!it) return null;
      const Icon = it.icon;
      const active = it.key === value;
      return (
        <button
          key={it.key}
          type="button"
          role="tab"
          aria-selected={active}
          className={active ? 'tab tab--active' : 'tab'}
          onClick={() => onChange?.(it.key)}
          disabled={it.disabled}
        >
          {Icon && <Icon size={14} />}
          {it.label}
          {(it.badge || it.badge === 0) && Number(it.badge) > 0 && (
            <span className="tab-badge">{it.badge}</span>
          )}
        </button>
      );
    })}
  </div>
);
